import { offer as offerQuery } from '@data/queries';
import { Container, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import imageUrlFor from '@utils/image';
import sanity from '@utils/sanity';
import { NextSeo } from 'next-seo';
import { ReactElement } from 'react';
import { OfferProps } from 'src/@types/offer';
import { useResponsive } from 'src/hooks';
import OfferContactPerson from 'src/sections/offers/OfferContactPerson';
import OfferDetails from 'src/sections/offers/OfferDetails';
import OfferForm from 'src/sections/offers/OfferForm';
import OfferGallery from 'src/sections/offers/OfferGallery';
import OfferHeader from 'src/sections/offers/OfferHeader';
import OfferSimilar from 'src/sections/offers/OfferSimilar';
import { fPrice, fUnit } from 'src/utils/formatPrice';
import { Breadcrumbs, Page } from '../../src/components';
import { HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT } from '../../src/config';
import Layout from '../../src/layouts';
import Routes from '../../src/routes';
import { Newsletter } from '../../src/sections/newsletter';

// ----------------------------------------------------------------------

const offersQuery = `*[_type == "offer"] { _id, slug, title, description, header, color, date, price, unit }`;

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: HEADER_MOBILE_HEIGHT,
  [theme.breakpoints.up('md')]: {
    paddingTop: HEADER_DESKTOP_HEIGHT,
  },
}));

const PaperStyle = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
}));

// ----------------------------------------------------------------------

type Props = {
  offer: OfferProps;
  offers: OfferProps[];
};

export default function OfferPage({ offer, offers }: Props) {
  const { title, gallery, price, unit, seo } = offer;

  const isDesktop = useResponsive('up', 'md');

  return (
    <>
      <NextSeo
        openGraph={{
          images: [{ url: imageUrlFor(offer.header).width(600).url() || '' }],
        }}
        canonical={'https://www.b-e-a.no/kampanjer/' + offer.slug.current}
      />
      <Page seo={seo}>
        <RootStyle>
          <Container sx={{ mb: 10 }}>
            <Breadcrumbs
              sx={{ mt: 5 }}
              links={[
                { name: 'Hjem', href: '/' },
                { name: 'Kampanjer', href: Routes.offers },
                { name: title },
              ]}
            />

            {gallery?.images.length > 0 && <OfferGallery gallery={gallery} />}

            <Grid container spacing={8} direction="row-reverse">
              {isDesktop && (
                <Grid item xs={12} md={5} lg={4}>
                  <Stack spacing={3}>
                    {price && (
                      <PaperStyle sx={{ borderRadius: 1, p: 3, bgcolor: 'warning.light' }}>
                        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                          Pristilbud
                        </Typography>
                        <Stack
                          direction="row"
                          alignItems="end"
                          sx={{ my: { sx: 2, sm: 0 } }}
                          spacing={1}
                        >
                          <Typography variant="h3" sx={{ lineHeight: 1.1 }}>
                            Kun kr {fPrice(price)},-
                          </Typography>
                          {unit && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {fUnit(unit)}
                            </Typography>
                          )}
                        </Stack>
                      </PaperStyle>
                    )}
                    <OfferForm offer={offer} />
                    <OfferContactPerson offer={offer} />
                  </Stack>
                </Grid>
              )}

              <Grid item xs={12} md={7} lg={8}>
                <OfferHeader offer={offer} />

                <Divider sx={{ borderStyle: 'dashed', my: 5 }} />

                {!isDesktop && price && (
                  <PaperStyle sx={{ borderRadius: 1, px: 3, mb: 4, bgcolor: 'warning.light' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                        Pristilbud
                      </Typography>
                      <Stack direction="row" alignItems="end" sx={{ my: 2 }} spacing={1}>
                        <Typography variant="h3" sx={{ lineHeight: 1.3 }}>
                          Kun Kr {fPrice(price)}
                        </Typography>
                        {unit && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {fUnit(unit)}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </PaperStyle>
                )}

                {!isDesktop && (
                  <Stack spacing={3} mb={4}>
                    <OfferForm offer={offer} />
                  </Stack>
                )}

                <OfferDetails offer={offer} />
              </Grid>
            </Grid>
          </Container>

          <OfferSimilar offers={offers.slice(1)} />
          <Newsletter />
        </RootStyle>
      </Page>
    </>
  );
}

// ----------------------------------------------------------------------

OfferPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

// -----------------------------------------------------------------------

type Params = {
  params: {
    slug: string;
  };
};

export const getStaticPaths = async () => {
  const offers = await sanity.fetch(offersQuery);
  const paths = offers.map((offer: OfferProps) => ({
    params: { slug: offer.slug.current },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

// This function gets called at build time on server-side.
export const getStaticProps = async ({ params }: Params) => {
  const offer = await sanity.fetch(offerQuery, { slug: params.slug });
  const offers = await sanity.fetch(offersQuery);
  return { props: { offer, offers } };
};
